import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { BarChart3, Eye, Grid3x3, List, Filter, Calendar, FileText, Link2, ToggleLeft, ToggleRight } from "lucide-react";
import toast from "react-hot-toast";
import { updateDoc, doc } from "firebase/firestore";

// SavedTest interface removed - using plain JavaScript objects

const MyTests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("list");
  const [dateFilter, setDateFilter] = useState("all");

  const getShareLink = (id) => `${window.location.origin}/test/share/${id}`;

  const copyShareLink = async (id) => {
    try {
      await navigator.clipboard.writeText(getShareLink(id));
      toast.success("Share link copied");
    } catch (error) {
      console.error("Failed to copy share link:", error);
      toast.error("Couldn't copy link. Please copy manually.");
    }
  };

  const toggleTestEnabled = async (testId, currentStatus) => {
    const db = getFirestore(firebaseApp);
    const newStatus = currentStatus === false ? true : !currentStatus; // Default to enabled if undefined

    try {
      await updateDoc(doc(db, "hrTests", testId), {
        isEnabled: !currentStatus
      });
      toast.success(newStatus ? "Test is now active and accepting submissions" : "Test h disabled");
    } catch (error) {
      console.error("Failed to toggle test status:", error);
      toast.error("Failed to update test status");
    }
  };

  useEffect(() => {
    if (!user) return;

    const db = getFirestore(firebaseApp);
    const q = query(
      collection(db, "hrTests"),
      where("hrId", "==", user.id),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTests(docs);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Filter tests based on date
  useEffect(() => {
    const now = new Date();
    let filtered = [...tests];

    if (dateFilter === "today") {
      filtered = tests.filter((test) => {
        if (!test.createdAt) return false;
        const testDate = test.createdAt.seconds
          ? new Date(test.createdAt.seconds * 1000)
          : new Date(test.createdAt);
        return testDate.toDateString() === now.toDateString();
      });
    } else if (dateFilter === "week") {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = tests.filter((test) => {
        if (!test.createdAt) return false;
        const testDate = test.createdAt.seconds
          ? new Date(test.createdAt.seconds * 1000)
          : new Date(test.createdAt);
        return testDate >= weekAgo;
      });
    } else if (dateFilter === "month") {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = tests.filter((test) => {
        if (!test.createdAt) return false;
        const testDate = test.createdAt.seconds
          ? new Date(test.createdAt.seconds * 1000)
          : new Date(test.createdAt);
        return testDate >= monthAgo;
      });
    }

    setFilteredTests(filtered);
  }, [tests, dateFilter]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="text-center py-10">Loading your tests...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Title and Actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">My Generated Tests</h1>
        <Link
          to="/hr/test-generator"
          className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
        >
          Generate New Test
        </Link>
      </div>

      {/* Filters and View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          {/* Date Filter */}
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value )}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            <span className="text-sm text-gray-600">
              {filteredTests.length} test{filteredTests.length !== 1 ? "s" : ""} found
            </span>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "list"
                  ? "bg-white text-red-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === "grid"
                  ? "bg-white text-red-800 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              title="Grid View"
            >
              <Grid3x3 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Tests Display */}
      {filteredTests.length === 0 ? (
        <div className="text-center py-20 bg-white border rounded-xl">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">
            {tests.length === 0
              ? "You haven't generated any tests yet."
              : "No tests found for the selected filter."}
          </p>
          {tests.length === 0 && (
            <Link
              to="/hr/test-generator"
              className="text-red-700 hover:underline mt-2 inline-block"
            >
              Generate your first test
            </Link>
          )}
        </div>
      ) : viewMode === "list" ? (
        <div className="space-y-4">
          {filteredTests.map((test) => (
            <div
              key={test.id}
              className="bg-white p-6 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {test.testData?.title || test.jobTitle || "Untitled Test"}
                  </h3>
                  {/* Status Badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    test.isEnabled === false
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {test.isEnabled === false ? 'Disabled' : 'Active'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Generated on {formatDate(test.createdAt)}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  {test.jobTitle && <span>Position</span>}
                  {test.config?.questionCount && (
                    <span>Questions</span>
                  )}
                  {test.config?.duration && (
                    <span>Duration min</span>
                  )}
                  {test.config?.difficulty && (
                    <span className="capitalize">
                      Difficulty
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 ml-4">
                {/* Enable/Disable Toggle */}
                <button
                  onClick={() => toggleTestEnabled(test.id, test.isEnabled)}
                  className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                    test.isEnabled === false
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title={test.isEnabled === false ? 'Enable test to accept submissions' : 'Disable test when hiring is complete'}
                >
                  {test.isEnabled === false ? (
                    <>
                      <ToggleLeft className="w-4 h-4 mr-2" />
                      Enable Test
                    </>
                  ) : (
                    <>
                      <ToggleRight className="w-4 h-4 mr-2" />
                      Disable Test
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate(`/hr/test-results/${test.id}`)}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Show Results
                </button>

                <Link
                  to={`/hr/my-tests/${test.id}`}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Test
                </Link>

                <button
                  onClick={() => copyShareLink(test.id)}
                  className="flex items-center justify-center px-4 py-2 border border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Share Link
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTests.map((test) => (
            <div
              key={test.id}
              className="bg-white p-6 border border-gray-200 rounded-xl shadow-sm hover:shadow-md hover:border-red-300 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-red-50 rounded-lg">
                  <FileText className="w-6 h-6 text-red-800" />
                </div>
                <div className="flex items-center gap-2">
                  {/* Status Badge */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    test.isEnabled === false
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {test.isEnabled === false ? 'Disabled' : 'Active'}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(test.createdAt).split(',')[0]}
                  </span>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 min-h-[3.5rem]">
                {test.testData?.title || test.jobTitle || "Untitled Test"}
              </h3>

              <div className="space-y-2 text-sm text-gray-600 mb-4">
                {test.jobTitle && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Position:</span>
                    <span className="font-medium truncate">{test.jobTitle}</span>
                  </div>
                )}
                {test.config?.questionCount && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Questions:</span>
                    <span className="font-medium">{test.config.questionCount}</span>
                  </div>
                )}
                {test.config?.duration && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Duration:</span>
                    <span className="font-medium">{test.config.duration} min</span>
                  </div>
                )}
                {test.config?.difficulty && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">Difficulty:</span>
                    <span className="font-medium capitalize">{test.config.difficulty}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
                {/* Enable/Disable Toggle */}
                <button
                  onClick={() => toggleTestEnabled(test.id, test.isEnabled)}
                  className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                    test.isEnabled === false
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  title={test.isEnabled === false ? 'Enable test to accept submissions' : 'Disable test when hiring is complete'}
                >
                  {test.isEnabled === false ? (
                    <>
                      <ToggleLeft className="w-4 h-4 mr-2" />
                      Enable Test
                    </>
                  ) : (
                    <>
                      <ToggleRight className="w-4 h-4 mr-2" />
                      Disable Test
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate(`/hr/test-results/${test.id}`)}
                  className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Show Results
                </button>

                <Link
                  to={`/hr/my-tests/${test.id}`}
                  className="flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Test
                </Link>

                <button
                  onClick={() => copyShareLink(test.id)}
                  className="flex items-center justify-center px-4 py-2 border border-dashed border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Share Link
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyTests;
